import { ApplicationConfig, ErrorHandler, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { provideApollo } from 'apollo-angular';
import { InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { provideMarkdown } from 'ngx-markdown';
import { provideAuth0 } from '@auth0/auth0-angular';
import { getAuth0Config } from './core/config/auth0.config';
import { EnvironmentService } from './core/config/environment.service';
import { authInterceptor, errorInterceptor } from './core/interceptors';
import { ChunkReloadErrorHandler } from './core/handlers/chunk-reload.error-handler';
import { AppBootstrapService } from './core/services/app-bootstrap.service';
import SynapseTheme from './theme/synapse-theme';
import { VOICE_CONFIG, DEFAULT_VOICE_CONFIG } from './core/services/voice/voice.config';

import { routes } from './app.routes';
import {provideTranslateService} from "@ngx-translate/core";
import { TranslateLoader } from "@ngx-translate/core";
import { FetchTranslateLoader } from './core/i18n/fetch-translate.loader';
import { DEFAULT_LANGUAGE, applyDocumentLanguage, resolveInitialLanguage } from './shared/utils/app-language';
// Se calcula una vez, antes de construir los providers: `<html lang>` queda fijado
// desde el primer frame y `provideTranslateService` ya pide el fichero correcto.
const initialLanguage = resolveInitialLanguage();
applyDocumentLanguage(initialLanguage);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // app#560: chunk lazy muerto tras un deploy (404) → recarga one-shot con
    // cooldown en vez de dejar la navegación rota hasta un F5 manual.
    { provide: ErrorHandler, useClass: ChunkReloadErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        // ORDEN CRÍTICO: errorInterceptor debe ir ANTES (ser exterior a)
        // authInterceptor. El 401 sintético de sesión muerta
        // (token_unavailable) se fabrica en el catchError de authInterceptor
        // y solo lo ven los interceptores registrados antes que él al
        // propagarse hacia el llamador; con el orden invertido, la
        // recuperación limpia de sesión muerta (app#558) no se ejecutaba jamás.
        errorInterceptor,
        authInterceptor
      ])
    ),
    provideTranslateService({
      // Cargador por `fetch`: el de HttpClient pasaba por el authInterceptor, que
      // cortaba la petición cuando la sesión no puede emitir token y dejaba la
      // interfaz pintando las claves en crudo.
      loader: { provide: TranslateLoader, useClass: FetchTranslateLoader },
      // El idioma se resuelve AQUÍ (lo guardado por el usuario, o el del navegador
      // la primera vez) y no en el AppComponent: arrancar en español para cambiar
      // a inglés un instante después hacía que la primera pintura saliera en
      // español. `fallbackLang` es el español porque es el idioma completo de
      // referencia: una clave que falte en `en.json` se ve en español, no en crudo.
      fallbackLang: DEFAULT_LANGUAGE,
      lang: initialLanguage
    }),
    provideMarkdown(),
    
    // Inicializar servicios de bootstrap
    {
      provide: APP_INITIALIZER,
      useFactory: (appBootstrap: AppBootstrapService) => () => appBootstrap.initialize(),
      deps: [AppBootstrapService],
      multi: true
    },
    
    provideAuth0((() => {
      const config = getAuth0Config();
      return {
        domain: config.domain,
        clientId: config.clientId,
        authorizationParams: {
          redirect_uri: config.redirectUri,
          audience: config.audience,
          scope: config.scope,
          // `organization` se envía SOLO si este despliegue está atado a un cliente
          // (`auth0OrganizationId` en runtime-env.js). Entonces Auth0 rechaza el login de
          // quien no sea miembro. Vacío = frontend multi-cliente y lo elige el usuario.
          //
          // El spread condicional NO es cosmética: `organization: ''` viajaría como
          // `organization=` en la URL de /authorize, que no es lo mismo que omitirlo.
          //
          // ⚠️ Surte efecto en el SIGUIENTE login, no al desplegar: quien tenga sesión
          // abierta conserva su refresh token (emitido sin contexto de organización) y
          // `getAccessTokenSilently` seguirá renovando el token viejo hasta que caduque o
          // cierre sesión. Es lo deseable — nadie se queda fuera a media faena—, pero
          // significa que para COMPROBAR el cambio hay que hacer logout primero.
          ...(config.organization ? { organization: config.organization } : {})
        },
        useRefreshTokens: config.useRefreshTokens,
        useRefreshTokensFallback: config.useRefreshTokensFallback,
        cacheLocation: config.cacheLocation,
        // Sin esto el SDK se lleva al usuario a `/` en cuanto Auth0 devuelve un error, y
        // de ahí el AuthGuard lo rebota a login: el rechazo queda MUDO. El por qué
        // completo, en auth0.config.ts. Ojo: este objeto enumera los campos uno a uno,
        // no hace spread de la config — un campo que no se liste aquí NO llega al SDK.
        errorPath: config.errorPath,
        // El OAuth del autoprovisioning (SYN-806) vuelve a /integrations/oauth/callback con
        // `?code=…&state=…`, la misma forma que el login de Auth0: sin esto el SDK lo toma por
        // SU callback, falla con «Invalid state» y manda al usuario a /callback. Aquí solo se
        // comprueba la sesión; el `code` es de Microsoft y lo consume la api.
        skipRedirectCallback: globalThis.location?.pathname?.startsWith('/integrations/oauth/callback') ?? false
      };
    })()),
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top'
      })
    ),
    // Sin provideClientHydration(): la app no tiene SSR, y declararlo junto a
    // withEnabledBlockingInitialNavigation() provocaba los warnings
    // NG05001/NG0505 en cada carga.

    provideApollo(() => {
      const httpLink = inject(HttpLink);
      const env = inject(EnvironmentService);

      return {
        link: httpLink.create({
          uri: env.graphqlEndpoint
        }),
        cache: new InMemoryCache({
          typePolicies: {
            Finding: { keyFields: ['correlation_id'] },
            Investigation: { keyFields: ['id'] },
            InvestigationRun: { keyFields: ['_id'] }
            // Las reglas Sigma viven embebidas (JSON) en Investigation.sigma_rules,
            // no son un tipo normalizado → no necesitan typePolicy propio.
          }
        }),
        connectToDevTools: false,
        defaultOptions: {
          watchQuery: {
            errorPolicy: 'all'
          },
          query: {
            errorPolicy: 'all'
          }
        }
      };
    }),
    { provide: VOICE_CONFIG, useValue: DEFAULT_VOICE_CONFIG },
    providePrimeNG({
      theme: {

        preset: SynapseTheme,
        options: {
          prefix: 'p',
          darkModeSelector: ':root[data-theme="dark"]',
          cssLayer: false
        }
      }
    })
  ]
};
